import { flexRender, useReactTable } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useRef } from 'react';

import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { ListPagination } from '@adflow/frontend/ui-layout';
import type { AssignableRef } from '@adflow/frontend/util-common';
import { TABLE_ROWS_COUNT_UNKNOWN } from '@adflow/frontend/util-router';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { CollapsableSidebarContainer } from './CollapsableSidebarContainer';
import type { DataTableColumnProps } from './column-template/types';
import { ColumnPadding } from './DataTableFull/ColumnPadding';
import { styles as dataTableFullStyles } from './DataTableFull/styles';
import type { DataTableRowsProps } from './DataTableRows';
import { DataTableRows } from './DataTableRows';
import { dataTableStyles } from './dataTableStyles';
import { getSortByToggleProps } from './getSortByToggleProps';
import type { DataTableSortProps } from './types';
import { useControlledTable } from './useControlledTable';

export const styles = dataTableStyles;

interface DataTableBaseProps<T>
	extends
		DataTableSortProps,
		DataTableColumnProps<T>,
		Omit<DataTableRowsProps<T>, 'rows'> {
	data: T[];
	tableWrapRef?: AssignableRef<HTMLDivElement | null>;
	sidebarStyle?: boolean;
	sidebarCollapsed?: boolean;

	isLoading?: boolean;
	isError?: boolean;
	renderOnError?: ReactNode | null;

	useColumnResizing?: boolean;
	onlySticky?: boolean;
	customFooter?: ReactNode;
	header?: ReactNode;
}

type DataTablePaginationProps =
	| {
			usePagination?: false;
	  }
	| {
			usePagination: true;

			currentPage: number;
			rowsPerPage: number;
			totalItems: number;
			itemType: DataTableItemType;
			onChangePage: (newPage: number) => void;
			onChangeRowsPerPage: (newItemsPerPage: number | null) => void;
	  };

export type DataTableProps<T> = DataTableBaseProps<T> &
	DataTablePaginationProps;

export function DataTable<T>(props: DataTableProps<T>) {
	const table = useReactTable<T>({
		_features: [ColumnPadding],
		columns: props.columns,
		data: props.data,

		...useControlledTable(props),
	});

	const paginationRef = useRef<HTMLDivElement>(null);
	const isPaginationRendered =
		props.usePagination && props.totalItems !== TABLE_ROWS_COUNT_UNKNOWN;

	return (
		<CollapsableSidebarContainer
			sidebarCollapsed={props.sidebarCollapsed}
			table={table}
			onlySticky={props.onlySticky}
		>
			{props.header}
			<div
				ref={props.tableWrapRef}
				css={[
					styles.tableWrap,
					{ flexGrow: 1 },
					props.sidebarStyle ? { overflowX: 'hidden' } : null,
				]}
			>
				{props.isError && !!props.renderOnError ? (
					<div css={{ width: '100%', height: '80%' }}>
						{props.renderOnError}
					</div>
				) : props.isLoading ? (
					<div css={{ width: '100%', height: '80%' }}>
						<LoadingIndicator />
					</div>
				) : (
					<table role="table" css={styles.table}>
						<thead css={styles.tableHead}>
							{table.getHeaderGroups().map((headerGroup) => {
								return (
									<tr key={headerGroup.id} role="row">
										{headerGroup.headers.map((header) => {
											if (!header.column.getIsPinned() && props.onlySticky)
												return null;
											return (
												<th
													key={header.id}
													role="columnheader"
													css={{ padding: 0 }}
													style={{ width: header.column.getSize() }}
												>
													<div
														css={{
															display: 'flex',
															height: 32,
															position: 'relative',
														}}
														style={{
															paddingLeft: header.column.getPaddingLeft(),
															paddingRight: header.column.getPaddingRight(),
														}}
													>
														<span
															{...getSortByToggleProps(
																header.column,
																props.setSort
															)}
															css={{ width: '100%' }}
														>
															{flexRender(
																header.column.columnDef.header,
																header.getContext()
															)}
														</span>
														{header.column.getCanResize() ? (
															<span
																css={dataTableFullStyles.resizerContainer(
																	Boolean(
																		header.column.getIsPinned() ||
																		header.column.parent?.getIsPinned()
																	),
																	header.isPlaceholder
																)}
																style={{ cursor: 'col-resize' }}
																onMouseDown={header.getResizeHandler()}
																onTouchStart={header.getResizeHandler()}
															>
																<div
																	css={dataTableFullStyles.resizer(
																		header.column.getIsResizing()
																	)}
																	id="resizer"
																/>
															</span>
														) : null}
													</div>
												</th>
											);
										})}

										{/* Force thead background to span the full table width */}
										<th role="presentation" css={{ position: 'absolute' }} />
									</tr>
								);
							})}
						</thead>
						<tbody>
							<DataTableRows
								{...props}
								rows={table.getRowModel().rows}
								isHeaderFooterRendered={
									isPaginationRendered ? paginationRef.current !== null : true
								}
							/>
						</tbody>
					</table>
				)}
			</div>
			{isPaginationRendered ? (
				<ListPagination
					containerRef={paginationRef}
					rowsPerPageOptions={[5, 10, 25, 50, 100]}
					totalItems={props.totalItems}
					rowsPerPage={props.rowsPerPage}
					page={props.currentPage}
					onChangePage={props.onChangePage}
					onChangeRowsPerPage={props.onChangeRowsPerPage}
					sidebarStyle={Boolean(props.sidebarStyle)}
					itemType={props.itemType}
				/>
			) : null}
			{props.customFooter}
		</CollapsableSidebarContainer>
	);
}
